import React from "react";
import {
  defineMessages,
  FormattedMessage as T,
  useIntl,
  IntlShape,
} from "react-intl";
import { RouteComponentProps } from "react-router-dom";
import { makeStyles, Theme } from "@material-ui/core/styles";
import Avatar from "@material-ui/core/Avatar";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import CircularProgress from "@material-ui/core/CircularProgress";
import Collapse from "@material-ui/core/Collapse";
import Container from "@material-ui/core/Container";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import IconButton from "@material-ui/core/IconButton";
import InputAdornment from "@material-ui/core/InputAdornment";
import Paper from "@material-ui/core/Paper";
import TextField from "@material-ui/core/TextField";
import Tooltip from "@material-ui/core/Tooltip";
import Alert, { AlertProps } from "@material-ui/lab/Alert";
import ExitToAppIcon from "@material-ui/icons/ExitToApp";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import LockIcon from "@material-ui/icons/Lock";
import PermIdentityIcon from "@material-ui/icons/PermIdentity";
import TapAndPlayIcon from "@material-ui/icons/TapAndPlay";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";
import clsx from "clsx";
import { AxiosError } from "axios";
import { Controller, useForm, ManualFieldError } from "react-hook-form";
import * as yup from "yup";

import { LoginError } from "../AuthProvider";
import FirstLoginForm, { FirstLoginData } from "./FirstLoginForm";
import FullscreenNotification, {
  FullscreenNotificationProps,
} from "../FullscreenNotification";
import loginBackgroundImage from "../../assets/backgrounds/login.jpg";
import useAuth from "../../hooks/useAuth";
import useSocketIo from "../../hooks/useSocketIo";
import { MustChangePasswordError } from "@egis-auth/client";
import Typography from "@material-ui/core/Typography";
import { version } from "../../../package.json";

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    background: `url(${loginBackgroundImage}) center center / cover no-repeat fixed`,
  },
  loginContainer: {
    position: "relative",
    width: "100%",
    padding: 24,
  },
  loginForm: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
    position: "relative",
  },
  tapAndPlayAvatar: {
    width: 96,
    height: 96,
    position: "absolute",
    top: -48,
    left: "calc(50% - 48px)",
    backgroundColor: theme.palette.getContrastText(theme.palette.primary.main),
    color: theme.palette.primary.main,
    boxShadow: theme.shadows[1],
  },
  tapAndPlayIcon: {
    width: 64,
    height: 64,
  },
  textField: {
    marginTop: 16,
  },
  collapse: {
    width: "100%",
  },
  expand: {
    margin: "16px 0",
    transform: "rotate(0deg)",
    transition: theme.transitions.create("transform", {
      duration: theme.transitions.duration.standard,
    }),
  },
  expandOpen: {
    transform: "rotate(180deg)",
  },
  loginFooter: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    alignItems: "center",
  },
  loginFooterAlert: {
    width: "100%",
  },
  loginFooterActions: {
    width: "100%",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginTop: 16,
  },
  header: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    position: "absolute",
    width: "100%",
    top: 46,
    padding: "8px 0",
    backgroundColor: "rgba(255,255,255,0.9)",
  },
  title: {
    "& >:first-child": {
      color: theme.palette.primary.dark,
      fontWeight: theme.typography.fontWeightRegular,
    },
    "& >:nth-child(2)": {
      color: theme.palette.primary.light,
      fontWeight: theme.typography.fontWeightMedium,
    },
    "& >:nth-child(3)": {
      ...theme.typography.caption,
      fontWeight: theme.typography.fontWeightLight,
    },
  },
}));

const msg = defineMessages({
  matriculeLabel: {
    id: "login.matricule.label",
    description: "Message displayed to labeled the matricule field.",
    defaultMessage: "Matricule",
  },
  matriculeRequired: {
    id: "login.matricule.required",
    description: "Message displayed when the matricule field is empty.",
    defaultMessage: "The matricule is required",
  },
  pinCodeLabel: {
    id: "login.pinCode.label",
    description: "Message displayed to labeled the pinCode field.",
    defaultMessage: "Pin code",
  },
  pinCodeRequired: {
    id: "login.pinCode.required",
    description: "Message displayed when the pin code field is empty.",
    defaultMessage: "The pin code is required",
  },
  cardUidLabel: {
    id: "login.cardUid.label",
    description: "Message displayed to labeled the cardUid field.",
    defaultMessage: "Card UID",
  },
  cardUidRequired: {
    id: "login.cardUid.required",
    description: "Message displayed when the card UID field is empty.",
    defaultMessage: "The card UID is required",
  },
  deviceIdLabel: {
    id: "login.deviceId.label",
    description: "Message displayed to labeled the deviceId field.",
    defaultMessage: "Device ID",
  },
  deviceIdRequired: {
    id: "login.deviceId.required",
    description: "Message displayed when the device ID field is empty.",
    defaultMessage: "The device ID is required",
  },
  showPasswordButtonTooltipLabel: {
    id: "login.showPasswordButtonTooltip.label",
    description: "Label of the show password button tooltip.",
    defaultMessage: "Show password",
  },
  hidePasswordButtonTooltipLabel: {
    id: "login.hidePasswordButtonTooltip.label",
    description: "Label of the hide password button tooltip.",
    defaultMessage: "Hide password",
  },
  expandButtonTooltipLabel: {
    id: "login.expandButtonTooltip.label",
    description: "Label of the expand button tooltip.",
    defaultMessage: "Show the NFC part",
  },
  shrinkButtonTooltipLabel: {
    id: "login.shrinkButtonTooltip.label",
    description: "Label of the shrink button tooltip.",
    defaultMessage: "Hide the NFC part",
  },
  keepSessionOpenCheckboxLabel: {
    id: "login.keepSessionOpenCheckbox.label",
    description: "Label of the keep session open checkbox.",
    defaultMessage: "Keep the session open",
  },
  submitButtonLabel: {
    id: "login.submitButton.label",
    description: "Label of the submit button.",
    defaultMessage: "Log in",
  },
  nfcReaderOffline: {
    id: "login.nfcReader.offline",
    description: "Message displayed when the NFC reader is offline.",
    defaultMessage: "NFC reader offline",
  },
  nfcReaderWaiting: {
    id: "login.nfcReader.waiting",
    description: "Message displayed when the NFC reader is waiting.",
    defaultMessage: "NFC reader waiting",
  },
  nfcScanRequired: {
    id: "login.nfcScan.required",
    description: "Message displayed when the NFC scan is required.",
    defaultMessage: "NFC scan required",
  },
  nfcScanSuccess: {
    id: "login.nfcScan.success",
    description: "Message displayed when the NFC scan is successful.",
    defaultMessage: "NFC scan successful",
  },
  [LoginError.INVSN]: {
    id: "login.loginError.invsn",
    description: "Message displayed when the device ID is invalid.",
    defaultMessage: "The device ID is invalid",
  },
  [LoginError.LOG]: {
    id: "login.loginError.log",
    description: "Message displayed when the matricule is invalid.",
    defaultMessage: "The matricule is invalid",
  },
  [LoginError.MP]: {
    id: "login.loginError.mp",
    description:
      "Message displayed when the matricule and/or the pin code is missing.",
    defaultMessage: "The matricule and/or the pin code is missing",
  },
  [LoginError.MSN]: {
    id: "login.loginError.msn",
    description: "Message displayed when the device ID is missing.",
    defaultMessage: "The device ID is missing",
  },
  [LoginError.PASS]: {
    id: "login.loginError.pass",
    description: "Message displayed when the pin code is invalid.",
    defaultMessage: "The pin code is invalid",
  },
  [LoginError.UN1]: {
    id: "login.loginError.un1",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  [LoginError.UN2]: {
    id: "login.loginError.un2",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  [LoginError.UN3]: {
    id: "login.loginError.un3",
    description: "Message displayed when the user is unauthorized.",
    defaultMessage: "Unauthorized",
  },
  loginErrorUnknown: {
    id: "login.loginError.unknown",
    description: "Message displayed when the login error is unknown.",
    defaultMessage: "Unknown login error",
  },
});

interface NfcScanEventData {
  readerName: string;
  deviceId: string;
  uid: string;
}

function getLoginFormSchema(y: typeof yup, t: IntlShape["formatMessage"]) {
  return y.object().shape({
    cardUid: y.string().required(t(msg.cardUidRequired)),
    deviceId: y.string().required(t(msg.deviceIdRequired)),
    matricule: y.string().required(t(msg.matriculeRequired)),
    pinCode: y.string().required(t(msg.pinCodeRequired)),
  });
}

type LoginFormValues = yup.InferType<ReturnType<typeof getLoginFormSchema>>;

const emptyLoginFormValues: LoginFormValues = {
  cardUid: "",
  deviceId: "",
  matricule: "",
  pinCode: "",
};

export type LoginProps = React.DetailedHTMLProps<
  React.HTMLAttributes<HTMLDivElement>,
  HTMLDivElement
> &
  RouteComponentProps;

function Login(props: LoginProps) {
  const { className, history, location, match, staticContext, ...other } =
    props;
  const classes = useStyles();
  const rootClasses = React.useMemo(() => {
    return clsx(classes.root, className);
  }, [className, classes.root]);
  const { formatMessage: t } = useIntl();
  const {
    control,
    errors,
    formState,
    handleSubmit,
    // register,
    setError,
    setValue,
    triggerValidation,
    // unregister
  } = useForm<LoginFormValues>({
    defaultValues: emptyLoginFormValues,
    validationSchema: getLoginFormSchema(yup, t),
  });
  const { login } = useAuth();
  const [io, connected] = useSocketIo("http://localhost:4001");
  const [expanded, setExpanded] = React.useState(false);
  const [keepSessionOpen, setKeepSessionOpen] = React.useState(false);
  const [loading, setLoading] = React.useState(false);
  const [notification, setNotification] =
    React.useState<FullscreenNotificationProps>({ open: false });
  const [showPassword, setShowPassword] = React.useState(false);
  const [changePassword, setChangePassword] = React.useState(false);
  const [changePasswordData, setChangePasswordData] =
    React.useState<FirstLoginData>();

  // Register the card fields if not registered in the markup.
  // React.useEffect(() => {
  //   register({ name: "cardUid" });
  //   register({ name: "deviceId" });

  //   return () => {
  //     unregister(["cardUid", "deviceId"]);
  //   };
  // }, [register, unregister]);

  // Binds NFC scan events via socket channel.
  React.useEffect(() => {
    io.off("nfc.scan");
    io.on("nfc.scan", (data: NfcScanEventData) => {
      setValue("cardUid", data.uid);
      setValue("deviceId", data.deviceId);
      triggerValidation(["cardUid", "deviceId"]);
    });
  }, [io, setValue, triggerValidation]);

  /**
   * Helper message to display in the login footer (mostly NFC related).
   */
  const helperText: AlertProps = React.useMemo(() => {
    const { dirtyFields } = formState;

    const nfcReaderOffline = !connected;
    const nfcScanError = !!errors.cardUid || !!errors.deviceId;
    const nfcReaderWaiting =
      !dirtyFields.has("cardUid") || !dirtyFields.has("deviceId");
    const nfcScanSuccess = !nfcScanError && !nfcReaderWaiting;

    if (nfcReaderOffline && !nfcScanSuccess) {
      return { severity: "warning", children: <T {...msg.nfcReaderOffline} /> };
    }

    if (!!errors.cardUid) {
      if (errors.cardUid.isManual) {
        return { severity: "error", children: errors.cardUid.message };
      }

      return { severity: "error", children: <T {...msg.nfcScanRequired} /> };
    }

    if (!!errors.deviceId) {
      if (errors.deviceId.isManual) {
        return { severity: "error", children: errors.deviceId.message };
      }

      return { severity: "error", children: <T {...msg.nfcScanRequired} /> };
    }

    if (nfcReaderWaiting) {
      return { severity: "info", children: <T {...msg.nfcReaderWaiting} /> };
    }

    return { severity: "success", children: <T {...msg.nfcScanSuccess} /> };
  }, [connected, errors.cardUid, errors.deviceId, formState]);

  /**
   * Manage the visibility of the password field.
   */
  const handleClickShowPassword = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setShowPassword(!showPassword);
    },
    [showPassword]
  );

  /**
   * Shows the expand button if process.env.REACT_APP_ENV is not 'production'.
   */
  const displayConnectionTooltip = process.env.REACT_APP_ENV !== "production";

  /**
   * Takes care of the expand state of the NFC part of the form.
   */
  const handleExpandButtonClick = React.useCallback(
    (_event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      setExpanded((prevExpanded) => !prevExpanded);
    },
    []
  );

  const handleKeepSessionOpenCheckboxChange = React.useCallback(
    (_event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
      setKeepSessionOpen(checked);
    },
    []
  );

  const handleNotificationClose = React.useCallback(
    (_event: React.SyntheticEvent<Element, Event>) => {
      setNotification({ open: false });
    },
    []
  );

  /**
   * Takes care of a login error.
   */
  const processLoginError = React.useCallback(
    (error: AxiosError) => {
      let fieldErrors: ManualFieldError<LoginFormValues>[] | undefined;
      let loginErrorMessage: FullscreenNotificationProps["message"] | undefined;
      switch (error.response?.data.code) {
        case LoginError.INVSN:
          fieldErrors = [
            {
              name: "deviceId",
              type: "loginError",
              message: <T {...msg[LoginError.INVSN]} />,
            },
          ];
          break;
        case LoginError.LOG:
          fieldErrors = [
            {
              name: "matricule",
              type: "loginError",
              message: <T {...msg[LoginError.LOG]} />,
            },
          ];
          break;
        case LoginError.MP:
          fieldErrors = [
            {
              name: "matricule",
              type: "loginError",
              message: <T {...msg[LoginError.MP]} />,
            },
            {
              name: "pinCode",
              type: "loginError",
              message: <T {...msg[LoginError.MP]} />,
            },
          ];
          break;
        case LoginError.MSN:
          fieldErrors = [
            {
              name: "deviceId",
              type: "loginError",
              message: <T {...msg[LoginError.MSN]} />,
            },
          ];
          break;
        case LoginError.PASS:
          fieldErrors = [
            {
              name: "pinCode",
              type: "loginError",
              message: <T {...msg[LoginError.PASS]} />,
            },
          ];
          break;
        case LoginError.UN1:
          loginErrorMessage = <T {...msg[LoginError.UN1]} />;
          break;
        case LoginError.UN2:
          loginErrorMessage = <T {...msg[LoginError.UN2]} />;
          break;
        case LoginError.UN3:
          loginErrorMessage = <T {...msg[LoginError.UN3]} />;
          break;
        default:
          loginErrorMessage = <T {...msg.loginErrorUnknown} />;
          break;
      }

      if (fieldErrors !== undefined) setError(fieldErrors);
      if (loginErrorMessage !== undefined) {
        setNotification({
          open: true,
          message: loginErrorMessage,
          type: "error",
        });
      }
    },
    [setError]
  );

  /**
   * Takes care of the submit process.
   */
  const handleLoginFormSubmit = React.useCallback(
    (data: LoginFormValues, _event?: React.BaseSyntheticEvent) => {
      setLoading(true);

      login(
        data.matricule,
        data.pinCode,
        data.deviceId,
        data.cardUid,
        keepSessionOpen ? "1" : "0"
      )
        .catch((error: AxiosError) => {
          if (error instanceof MustChangePasswordError) {
            setChangePasswordData({
              ...data,
              keepSessionOpen,
              token: error.token,
            });
            setChangePassword(true);
          } else {
            processLoginError(error);
          }
        })
        .finally(() => setLoading(false));
    },
    [keepSessionOpen, login, processLoginError]
  );

  return (
    <div className={rootClasses} {...other}>
      <div className={classes.header}>
        <Typography className={classes.title} variant="h2">
          <span>Image</span> <span>Review</span>{" "}
          <span>
            {version} {process.env.REACT_APP_VERSION_CODENAME}
          </span>
        </Typography>
      </div>
      <Container maxWidth="sm">
        <Paper className={classes.loginContainer}>
          <Avatar className={classes.tapAndPlayAvatar}>
            <TapAndPlayIcon className={classes.tapAndPlayIcon} />
          </Avatar>
          {!changePassword ? (
            <form
              className={classes.loginForm}
              onSubmit={handleSubmit(handleLoginFormSubmit)}
            >
              <Controller
                name="matricule"
                as={
                  <TextField
                    className={classes.textField}
                    helperText={!!errors.matricule && errors.matricule.message}
                    label={t(msg.matriculeLabel)}
                    required={errors.matricule?.type === "required"}
                    error={!!errors.matricule}
                    autoFocus
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PermIdentityIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                }
                control={control}
              />
              <Controller
                name="pinCode"
                as={
                  <TextField
                    className={classes.textField}
                    helperText={!!errors.pinCode && errors.pinCode.message}
                    label={t(msg.pinCodeLabel)}
                    type={showPassword ? "text" : "password"}
                    required={errors.pinCode?.type === "required"}
                    error={!!errors.pinCode}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip
                            title={
                              showPassword
                                ? t(msg.hidePasswordButtonTooltipLabel)
                                : t(msg.showPasswordButtonTooltipLabel)
                            }
                          >
                            <IconButton
                              aria-label={
                                showPassword
                                  ? t(msg.hidePasswordButtonTooltipLabel)
                                  : t(msg.showPasswordButtonTooltipLabel)
                              }
                              edge="end"
                              onClick={handleClickShowPassword}
                            >
                              {showPassword ? (
                                <VisibilityIcon color="secondary" />
                              ) : (
                                <VisibilityOffIcon color="secondary" />
                              )}
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                }
                control={control}
              />
              <Collapse className={classes.collapse} in={expanded}>
                <Controller
                  name="cardUid"
                  as={
                    <TextField
                      className={classes.textField}
                      helperText={!!errors.cardUid && errors.cardUid.message}
                      label={t(msg.cardUidLabel)}
                      required={errors.cardUid?.type === "required"}
                      error={!!errors.cardUid}
                      fullWidth
                      InputProps={{
                        readOnly: !displayConnectionTooltip && !connected,
                        startAdornment: (
                          <InputAdornment position="start">
                            <TapAndPlayIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                  control={control}
                />
                <Controller
                  name="deviceId"
                  as={
                    <TextField
                      className={classes.textField}
                      helperText={!!errors.deviceId && errors.deviceId.message}
                      label={t(msg.deviceIdLabel)}
                      required={errors.deviceId?.type === "required"}
                      error={!!errors.deviceId}
                      fullWidth
                      InputProps={{
                        readOnly: !displayConnectionTooltip && !connected,
                        startAdornment: (
                          <InputAdornment position="start">
                            <TapAndPlayIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  }
                  control={control}
                />
              </Collapse>
              {displayConnectionTooltip && (
                <Tooltip
                  title={
                    expanded
                      ? t(msg.shrinkButtonTooltipLabel)
                      : t(msg.expandButtonTooltipLabel)
                  }
                >
                  <IconButton
                    className={clsx(classes.expand, {
                      [classes.expandOpen]: expanded,
                    })}
                    aria-expanded={expanded}
                    aria-label={
                      expanded
                        ? t(msg.shrinkButtonTooltipLabel)
                        : t(msg.expandButtonTooltipLabel)
                    }
                    onClick={handleExpandButtonClick}
                  >
                    <ExpandMoreIcon />
                  </IconButton>
                </Tooltip>
              )}
              <div className={classes.loginFooter}>
                <Alert className={classes.loginFooterAlert} {...helperText} />
                <div className={classes.loginFooterActions}>
                  <FormControlLabel
                    label={t(msg.keepSessionOpenCheckboxLabel)}
                    control={
                      <Checkbox
                        checked={keepSessionOpen}
                        onChange={handleKeepSessionOpenCheckboxChange}
                      />
                    }
                  />
                  <Button
                    color="primary"
                    variant="contained"
                    type="submit"
                    startIcon={
                      formState.isSubmitting || loading ? (
                        <CircularProgress size={20} />
                      ) : (
                        <ExitToAppIcon />
                      )
                    }
                    disabled={formState.isSubmitting || loading}
                  >
                    <T {...msg.submitButtonLabel} />
                  </Button>
                </div>
              </div>
            </form>
          ) : (
            <FirstLoginForm loginData={changePasswordData} />
          )}
        </Paper>
      </Container>
      <FullscreenNotification
        onCloseButtonClick={handleNotificationClose}
        {...notification}
      />
    </div>
  );
}

export default Login;
